import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Avatar, Box, Button, Callout, Card, DataListItem, toast } from '@node-space/ui-components'
import { RequestErrorCallout } from 'components/Errors/RequestErrorCallout'
import { yupFormResolver } from 'hooks/forms/yupFormResolver'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { logSentryErrorAndGetTraceId } from 'utils/sentry'
import { generateReference } from 'utils/utils'
import { EARN_REFERENCE_PREFIX } from '../../../constants/const'
import { useEarnSheetContext } from '../../../context/EarnSheetProvider'
import { useCreatePortfolio } from '../../../hooks/api/useCreatePortfolio'
import { useDepositPortfolio } from '../../../hooks/api/useDepositPortfolio'
import { useEarnTracking } from '../../../hooks/useEarnTracking'
import { EarnDepositFormData, TransferStatus } from '../../../types'
import { invalidateEarnQueries, prepareDepositReviewData } from '../../../utils/utils'
import { EarnSheetAmountInput } from '../EarnSheetAmountInput'
import { EarnSheetCurrencySelect } from '../EarnSheetCurrencySelect'
import { EarnSheetReview } from '../EarnSheetReview'
import { EarnSheetWalletSelect } from '../EarnSheetWalletSelect'
import { getEarnDepositValidationSchema } from '../validationSchema'
import { EarnSheetDepositTermsCheckbox } from './EarnSheetDepositTermsCheckbox'

type DepositStep = 'form' | 'review'

export const EarnSheetDeposit = () => {
  const { t } = useTranslation()
  const { requestError, setRequestError } = useMappedErrorMessage()
  const reactQueryClient = useQueryClient()
  const { trackFormInitiate, trackFormCancel } = useEarnTracking()

  const { earnSheetAction, earnProducts, handleSheetUpdate } = useEarnSheetContext()
  const { selectedCurrency: selectedCurrencyFromAction, onClose } = earnSheetAction || {}
  const [maxAmount, setMaxAmount] = useState('')
  const [currentStep, setCurrentStep] = useState<DepositStep>('form')
  const depositReferenceRef = useRef('')
  const isConfirmingDepositRef = useRef(false)

  useEffect(() => {
    trackFormInitiate('deposit', { currency: selectedCurrencyFromAction })
  }, [trackFormInitiate, selectedCurrencyFromAction])

  const showCurrencySelect = !selectedCurrencyFromAction && earnProducts.length > 1
  const defaultCurrency =
    selectedCurrencyFromAction || (earnProducts.length === 1 ? earnProducts[0].asset : '')

  const { depositToPortfolio, isDepositingToPortfolioPending } = useDepositPortfolio({
    onSuccess: data => {
      invalidateEarnQueries(reactQueryClient, data.portfolioId)

      if (data.status === TransferStatus.FAILED) {
        setRequestError({
          show: true,
          handledMessage: t('earn.depositSheet.depositStatusFailed'),
        })
        return
      }

      onClose?.()
      toast({
        variant: 'success',
        header: t('earn.depositSheet.successNotificationTitle', {
          currency: `${data.depositAmount.amount} ${data.depositAmount.currency}`,
        }),
      })
    },
    onError: error => {
      const sentryTraceId = logSentryErrorAndGetTraceId(
        `Failed API request to /earn/deposit with status ${error?.status}`,
        error,
        { function: 'depositToPortfolio' }
      )
      setRequestError({
        show: true,
        errorCode: error?.status,
        sentryTraceId,
        handledMessage: t('earn.depositSheet.depositFailed'),
      })
    },
  })

  const { createPortfolio, isCreatingPortfolioPending } = useCreatePortfolio({
    onError: error => {
      const sentryTraceId = logSentryErrorAndGetTraceId(
        `Failed API request to /earn/portfolio with status ${error?.status}`,
        error,
        { function: 'createPortfolio' }
      )
      setRequestError({
        show: true,
        errorCode: error?.status,
        sentryTraceId,
        handledMessage: t('earn.depositSheet.portfolioCreationFailed'),
      })
    },
  })

  const isPending = isCreatingPortfolioPending || isDepositingToPortfolioPending

  const formMethods = useForm<EarnDepositFormData>({
    resolver: yupFormResolver<EarnDepositFormData>(getEarnDepositValidationSchema(t, maxAmount)),
    mode: 'onChange',
    defaultValues: {
      currency: defaultCurrency,
      allocateFrom: null,
      amount: '',
      termsAccepted: false,
    },
  })
  const {
    handleSubmit,
    formState: { isSubmitting },
    setValue,
    watch,
  } = formMethods

  const selectedCurrency = watch('currency')
  const selectedAsset = earnProducts.find(asset => asset.asset === selectedCurrency)
  const selectedWalletId = watch('allocateFrom')?.value

  useEffect(() => {
    if (!selectedCurrencyFromAction && selectedCurrency) {
      setValue('allocateFrom', null)
      setValue('amount', '')
      setMaxAmount('')
    }
  }, [selectedCurrency, selectedCurrencyFromAction, setValue])

  useEffect(() => {
    setValue('amount', '')
  }, [selectedWalletId, setValue])

  const onReviewBack = useCallback(() => {
    setCurrentStep('form')
  }, [])

  const hasSheetAction = !!earnSheetAction

  useEffect(() => {
    if (!hasSheetAction) return
    if (currentStep === 'review') {
      handleSheetUpdate({
        header: t('earn.depositSheet.reviewHeader'),
        onBack: onReviewBack,
      })
    } else {
      handleSheetUpdate({
        header: t('earn.depositSheet.header'),
        onBack: undefined,
      })
    }
  }, [currentStep, t, handleSheetUpdate, hasSheetAction, onReviewBack])

  const onCancel = () => {
    trackFormCancel('deposit', { currency: selectedCurrency })
    onClose?.()
  }

  const onSubmit = () => {
    setRequestError({
      show: false,
    })
    depositReferenceRef.current = generateReference(EARN_REFERENCE_PREFIX)
    setCurrentStep('review')
  }

  const onConfirmDeposit = async () => {
    if (isConfirmingDepositRef.current) return
    isConfirmingDepositRef.current = true

    try {
      setRequestError({
        show: false,
      })
      const data = formMethods.getValues()
      const walletId = data.allocateFrom?.value!
      const reference = depositReferenceRef.current

      let currentPortfolioId = selectedAsset?.portfolioId ?? undefined

      if (!currentPortfolioId) {
        const earningProductId = selectedAsset?.productId

        if (!earningProductId) {
          setRequestError({
            show: true,
            errorCode: 400,
            handledMessage: t('earn.depositSheet.earningProductNotFound'),
          })
          return
        }

        try {
          const createdPortfolio = await createPortfolio(earningProductId, `${reference}-portfolio`)
          currentPortfolioId = createdPortfolio?.id
        } catch {
          // Intentionally ignored: useCreatePortfolio re-throws after its onError
          // callback already set requestError, so we just stop this submit flow here.
          return
        }
      }

      depositToPortfolio({
        reference,
        portfolioId: currentPortfolioId,
        originator: {
          walletId,
          amount: data.amount,
        },
      })
    } finally {
      isConfirmingDepositRef.current = false
    }
  }

  if (currentStep === 'review') {
    return (
      <EarnSheetReview
        data={prepareDepositReviewData(formMethods.getValues())}
        requestError={requestError}
        isPending={isPending}
        onCancel={onCancel}
        onConfirm={onConfirmDeposit}
      />
    )
  }

  return (
    <FormProvider {...formMethods}>
      <Box
        tag="form"
        flexCol
        gapY={16}
        width="full"
        paddingY={24}
        onSubmit={handleSubmit(onSubmit)}
      >
        {requestError?.show && <RequestErrorCallout requestError={requestError} />}

        {showCurrencySelect && <EarnSheetCurrencySelect disabled={isPending} />}

        {!showCurrencySelect && selectedCurrency && (
          <Card variant="static" emphasis full>
            <DataListItem
              id="deposit-into"
              testId="deposit-into"
              title={t('earn.depositInto')}
              BodyAvatar={<Avatar type="currency" name={selectedCurrency} size="small" />}
              body={selectedCurrency}
              layout="vertical"
            />
          </Card>
        )}

        <EarnSheetWalletSelect
          selectedCurrency={selectedCurrency}
          name="allocateFrom"
          disabled={!selectedCurrency || isPending}
          testId="allocate-from-combobox"
          label={t('earn.depositSheet.depositFrom')}
          setMaxAmount={setMaxAmount}
        />

        <EarnSheetAmountInput
          selectedCurrency={selectedCurrency}
          maxAmount={maxAmount}
          disabled={!selectedCurrency || isPending}
        />

        <Callout variant="warning">{t('earn.depositSheet.cooldownDescription')}</Callout>

        <EarnSheetDepositTermsCheckbox disabled={isPending} />

        <Box flex justify="between" gapX={12} paddingT={24}>
          <Button onClick={onCancel} variant="secondary" type="button" testId="cancel-button">
            {t('cancel')}
          </Button>

          <Button
            variant="primary"
            type="submit"
            testId="deposit-button"
            disabled={isSubmitting || isPending}
            loading={isSubmitting || isPending}
          >
            {t('earn.deposit')}
          </Button>
        </Box>
      </Box>
    </FormProvider>
  )
}
